import React, { useState, useEffect } from "react";
import { FaTrash, FaSpinner, FaRedo } from "react-icons/fa";

const AdminPanel = () => {
  const [slides, setSlides] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [newSlide, setNewSlide] = useState({ alt: "" });
  const [newMaterial, setNewMaterial] = useState({
    alt: "",
    page: "",
    description: "",
    alts: [],
    descriptions: [],
  });
  const [newCatalog, setNewCatalog] = useState({
    title: "",
    description: "",
  });
  const [pageDescriptions, setPageDescriptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    id: null,
    type: "",
  });
  const [files, setFiles] = useState([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [expandedPages, setExpandedPages] = useState({});
  const [lastAddedId, setLastAddedId] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [pages, setPages] = useState([]);
  const [imageRotations, setImageRotations] = useState({});
  const [editingImageRotation, setEditingImageRotation] = useState(0);
  const [editingImageData, setEditingImageData] = useState(null);
  const [catalogUploading, setCatalogUploading] = useState(false);

  const fetchSlides = () => {
    setLoading(true);
    fetch(process.env.REACT_APP_API_URL + `/api/slides`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setSlides(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching slides:", error);
        setLoading(false);
      });
  };

  const fetchMaterials = () => {
    setLoading(true);
    fetch(process.env.REACT_APP_API_URL + `/api/materials`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setMaterials(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching materials:", error);
        setLoading(false);
      });
  };

  const fetchPageDescriptions = () => {
    fetch(process.env.REACT_APP_API_URL + `/api/page-descriptions`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        const descriptions = {};
        const pageNames = [];
        data.forEach((item) => {
          descriptions[item.page] = item.description;
          pageNames.push(item.page);
        });
        setPageDescriptions(descriptions);
        setPages(pageNames);
      })
      .catch((error) =>
        console.error("Error fetching page descriptions:", error)
      );
  };

  const fetchCatalogs = () => {
    setLoading(true);
    fetch(process.env.REACT_APP_API_URL + `/api/catalogs`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setCatalogs(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching catalogs:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSlides();
    fetchMaterials();
    fetchPageDescriptions();
    fetchCatalogs();
  }, []);

  useEffect(() => {
    if (pages.length > 0) {
      setExpandedPages({ [pages[0]]: true });
    }
  }, [pages]);

  const handleSlideSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // 檢查文件大小
    const fileInput = e.target.querySelector('input[type="file"]');
    if (fileInput.files[0] && fileInput.files[0].size > 10 * 1024 * 1024) {
      setMessage("文件大小超過10MB限制");
      return;
    }

    fetch(process.env.REACT_APP_API_URL + `/api/slides`, {
      method: "POST",
      body: formData,
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        fetchSlides();
        setNewSlide({ alt: "" });
        setMessage("輪播圖添加成功！");
        setLastAddedId(data.id);
        setTimeout(() => setLastAddedId(null), 3000); // 3秒後取消高亮
      })
      .catch(() => setMessage("輪播圖添加失敗！"));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);

    // 檢查文件大小 (10MB = 10 * 1024 * 1024 bytes)
    const maxFileSize = 10 * 1024 * 1024;
    const oversizedFiles = newFiles.filter((file) => file.size > maxFileSize);

    if (oversizedFiles.length > 0) {
      setMessage(
        `以下文件超過10MB限制：${oversizedFiles.map((f) => f.name).join(", ")}`
      );
      e.target.value = ""; // 清空文件選擇
      return;
    }

    // 檢查文件數量
    if (newFiles.length > 10) {
      setMessage("最多只能選擇10個文件");
      e.target.value = ""; // 清空文件選擇
      return;
    }

    // 檢查文件類型
    const invalidFiles = newFiles.filter(
      (file) => !file.type.startsWith("image/")
    );
    if (invalidFiles.length > 0) {
      setMessage(
        `以下文件不是圖片格式：${invalidFiles.map((f) => f.name).join(", ")}`
      );
      e.target.value = ""; // 清空文件選擇
      return;
    }

    setFiles(newFiles);
    setNewMaterial({
      ...newMaterial,
      alts: newFiles.map(() => ""),
      descriptions: newFiles.map(() => ""),
    });
    // 重置旋轉狀態
    setImageRotations({});
  };

  // 圖片旋轉處理函數
  const rotateImage = (file, rotation = 0) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // 根據旋轉角度調整canvas尺寸
        if (rotation === 90 || rotation === 270) {
          canvas.width = img.height;
          canvas.height = img.width;
        } else {
          canvas.width = img.width;
          canvas.height = img.height;
        }

        // 旋轉並繪製圖片
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        // 轉換為blob
        canvas.toBlob(resolve, file.type, 0.9);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleRotateImage = async (index) => {
    const currentRotation = imageRotations[index] || 0;
    const newRotation = (currentRotation + 90) % 360;

    try {
      const rotatedFile = await rotateImage(files[index], newRotation);
      const newFiles = [...files];
      newFiles[index] = rotatedFile;
      setFiles(newFiles);

      setImageRotations((prev) => ({
        ...prev,
        [index]: newRotation,
      }));
    } catch (error) {
      console.error("圖片旋轉失敗:", error);
      setMessage("圖片旋轉失敗！");
    }
  };

  const handleAltChange = (index, value) => {
    const newAlts = [...newMaterial.alts];
    newAlts[index] = value;
    setNewMaterial({ ...newMaterial, alts: newAlts });
  };

  const handleDescriptionChange = (index, value) => {
    const newDescriptions = [...newMaterial.descriptions];
    newDescriptions[index] = value;
    setNewMaterial({ ...newMaterial, descriptions: newDescriptions });
  };

  const handleMaterialSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append("images", file);
    });
    formData.append("alts", JSON.stringify(newMaterial.alts));
    formData.append("page", newMaterial.page);
    formData.append("descriptions", JSON.stringify(newMaterial.descriptions));

    fetch(process.env.REACT_APP_API_URL + `/api/materials`, {
      method: "POST",
      body: formData,
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw err;
          });
        }
        return response.json();
      })
      .then((data) => {
        fetchMaterials();
        setNewMaterial({ alts: [], page: "", descriptions: [] });
        setFiles([]);
        setMessage("材料圖片添加成功！");
        setLastAddedId(data.id);
        setTimeout(() => setLastAddedId(null), 3000); // 3秒後取消高亮
      })
      .catch((error) => {
        console.error("Error:", error);
        // 處理不同類型的錯誤
        if (error.error) {
          setMessage(error.error);
        } else if (error.message) {
          setMessage(error.message);
        } else {
          setMessage("材料圖片添加失敗！");
        }
      });
  };

  const handlePageDescriptionSubmit = (e, page) => {
    e.preventDefault();
    const description = pageDescriptions[page];
    fetch(process.env.REACT_APP_API_URL + `/api/page-descriptions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ page, description }),
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(() => {
        setMessage("頁面說明更新成功！");
      })
      .catch(() => setMessage("頁面說明更新失敗！"));
  };

  const handleDelete = () => {
    setLoading(true);
    fetch(
      process.env.REACT_APP_API_URL +
        `/api/${confirmDelete.type}s/${confirmDelete.id}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(() => {
        if (confirmDelete.type === "slide") {
          fetchSlides();
        } else if (confirmDelete.type === "material") {
          fetchMaterials();
        } else if (confirmDelete.type === "catalog") {
          fetchCatalogs();
        }
        setConfirmDelete({ show: false, id: null, type: "" });
        let typeName = "項目";
        if (confirmDelete.type === "slide") typeName = "輪播圖";
        else if (confirmDelete.type === "material") typeName = "材料圖片";
        else if (confirmDelete.type === "catalog") typeName = "型錄";
        setMessage(`${typeName}刪除成功！`);
      })
      .catch((error) => {
        console.error(`Error deleting ${confirmDelete.type}:`, error);
        setMessage(`刪除失敗：${error.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setEditingImageRotation(0); // 重置旋轉角度
    setEditingImageData(null); // 重置圖片數據
  };

  const handleEditImageRotate = async () => {
    if (!editingMaterial) return;

    const newRotation = (editingImageRotation + 90) % 360;

    try {
      // 獲取原始圖片數據
      const imageUrl = `${
        process.env.REACT_APP_API_URL
      }/uploads/${editingMaterial.image.split("/").pop()}`;
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // 創建圖片對象並旋轉
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // 根據旋轉角度調整canvas尺寸
        if (newRotation === 90 || newRotation === 270) {
          canvas.width = img.height;
          canvas.height = img.width;
        } else {
          canvas.width = img.width;
          canvas.height = img.height;
        }

        // 旋轉並繪製圖片
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((newRotation * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        // 轉換為blob並保存
        canvas.toBlob(
          (rotatedBlob) => {
            setEditingImageData(rotatedBlob);
            setEditingImageRotation(newRotation);
          },
          blob.type,
          0.9
        );
      };

      img.src = URL.createObjectURL(blob);
    } catch (error) {
      console.error("圖片旋轉預覽失敗:", error);
      setMessage("圖片旋轉預覽失敗！");
    }
  };

  const handleUpdateMaterial = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);

    // 確保 alt 和 description 不為 null
    const alt = formData.get("alt") || "";
    const description = formData.get("description") || "";
    console.log(alt, description);

    try {
      // 如果有旋轉的圖片數據，先上傳旋轉後的圖片
      if (editingImageData) {
        const uploadFormData = new FormData();
        uploadFormData.append("image", editingImageData, "rotated_image.jpg");
        uploadFormData.append("alt", alt);
        uploadFormData.append("description", description);

        const uploadResponse = await fetch(
          process.env.REACT_APP_API_URL +
            `/api/materials/${editingMaterial.id}/update-with-image`,
          {
            method: "PUT",
            body: uploadFormData,
            credentials: "include",
          }
        );

        if (!uploadResponse.ok) {
          throw new Error("Network response was not ok");
        }
      } else {
        // 沒有圖片旋轉，只更新文字
        const response = await fetch(
          process.env.REACT_APP_API_URL +
            `/api/materials/${editingMaterial.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ alt, description }),
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
      }

      fetchMaterials();
      setEditingMaterial(null);
      setEditingImageData(null);
      setEditingImageRotation(0);
      setMessage("材料圖片更新成功！");
    } catch (error) {
      console.error("Error updating material:", error);
      setMessage(`更新失敗：${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleDescription = (index) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const togglePageExpand = (page) => {
    setExpandedPages((prev) => ({
      ...prev,
      [page]: !prev[page],
    }));
  };

  // 型錄管理函數
  const handleCatalogSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // 檢查文件大小
    const fileInput = e.target.querySelector('input[type="file"]');
    if (fileInput.files[0] && fileInput.files[0].size > 50 * 1024 * 1024) {
      setMessage("文件大小超過50MB限制");
      return;
    }

    setCatalogUploading(true);

    fetch(process.env.REACT_APP_API_URL + `/api/catalogs`, {
      method: "POST",
      body: formData,
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        fetchCatalogs();
        setNewCatalog({ title: "", description: "" });
        setMessage("型錄添加成功！");
        setLastAddedId(data.id);
        setTimeout(() => setLastAddedId(null), 3000);
        // 重置表單
        e.target.reset();
      })
      .catch(() => setMessage("型錄添加失敗！"))
      .finally(() => {
        setCatalogUploading(false);
      });
  };

  const handleEditCatalog = (catalog) => {
    setEditingCatalog(catalog);
  };

  const handleUpdateCatalog = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);

    try {
      const title = formData.get("title");
      const description = formData.get("description");
      const file = formData.get("file");

      if (file && file.size > 0) {
        // 有新檔案上傳
        const response = await fetch(
          process.env.REACT_APP_API_URL +
            `/api/catalogs/${editingCatalog.id}/update-file`,
          {
            method: "PUT",
            body: formData,
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
      } else {
        // 只更新文字
        const response = await fetch(
          process.env.REACT_APP_API_URL + `/api/catalogs/${editingCatalog.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ title, description }),
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
      }

      fetchCatalogs();
      setEditingCatalog(null);
      setMessage("型錄更新成功！");
    } catch (error) {
      console.error("Error updating catalog:", error);
      setMessage(`更新失敗：${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCatalogDownload = async (catalog) => {
    if (!catalog.file_path) {
      setMessage("此型錄暫無檔案可下載");
      return;
    }

    try {
      // 使用 fetch 獲取檔案
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${catalog.file_path}`
      );

      if (!response.ok) {
        throw new Error("檔案下載失敗");
      }

      // 獲取檔案 blob
      const blob = await response.blob();

      // 獲取檔案名稱（從路徑中提取或使用標題）
      const fileName =
        catalog.file_path.split("/").pop() || `${catalog.title}.pdf`;

      // 創建下載連結
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;

      // 隱藏連結並觸發下載
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("下載失敗:", error);
      setMessage("檔案下載失敗，請稍後再試");
    }
  };

  // 檢查檔案是否為圖片
  const isImageFile = (filePath) => {
    if (!filePath) return false;
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
    const fileExtension = filePath
      .toLowerCase()
      .substring(filePath.lastIndexOf("."));
    return imageExtensions.includes(fileExtension);
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-8 text-center">後台管理系統</h1>
      {message && (
        <div
          className="fixed top-0 left-0 right-0 bg-blue-500 text-white px-4 py-3 text-center transition-all duration-300 ease-in-out z-50"
          style={{ transform: message ? "translateY(0)" : "translateY(-100%)" }}
        >
          <span className="block sm:inline">{message}</span>
        </div>
      )}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold mb-2">輪播圖管理</h2>
          <form onSubmit={handleSlideSubmit} className="mb-4">
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="image"
              >
                選擇圖片
              </label>
              <input
                type="file"
                name="image"
                required
                accept="image/*"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
              <p className="text-xs text-gray-500 mt-1">
                * 最大10MB，僅支援圖片格式
              </p>
            </div>
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="alt"
              >
                圖片描述
              </label>
              <input
                type="text"
                value={newSlide.alt}
                onChange={(e) =>
                  setNewSlide({ ...newSlide, alt: e.target.value })
                }
                placeholder="圖片描述"
                required
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              添加新輪播圖
            </button>
          </form>
          {loading ? (
            <div className="flex justify-center">
              <FaSpinner className="animate-spin text-4xl text-blue-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {slides.map((slide) => (
                <div
                  key={slide.id}
                  className={`border p-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg ${
                    slide.id === lastAddedId ? "bg-yellow-100" : ""
                  }`}
                >
                  <img
                    src={`${process.env.REACT_APP_API_URL}/uploads/${slide.image
                      .split("/")
                      .pop()}`}
                    alt={slide.alt}
                    className="mb-2 w-full h-48 object-cover rounded-lg"
                  />
                  <p className="text-gray-700 text-base">{slide.alt}</p>
                  <button
                    onClick={() =>
                      setConfirmDelete({
                        show: true,
                        id: slide.id,
                        type: "slide",
                      })
                    }
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mt-2 flex items-center justify-center transition duration-300 ease-in-out"
                  >
                    <FaTrash className="mr-2" /> 刪除
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 型錄管理區塊 */}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold mb-2">型錄管理</h2>
          <form onSubmit={handleCatalogSubmit} className="mb-4">
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="title"
              >
                型錄標題
              </label>
              <input
                type="text"
                name="title"
                value={newCatalog.title}
                onChange={(e) =>
                  setNewCatalog({ ...newCatalog, title: e.target.value })
                }
                placeholder="型錄標題"
                required
                disabled={catalogUploading}
                className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                  catalogUploading ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              />
            </div>
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="description"
              >
                型錄描述
              </label>
              <textarea
                name="description"
                value={newCatalog.description}
                onChange={(e) =>
                  setNewCatalog({ ...newCatalog, description: e.target.value })
                }
                placeholder="型錄描述"
                disabled={catalogUploading}
                className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-20 ${
                  catalogUploading ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              />
            </div>
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="file"
              >
                選擇檔案（可選）
              </label>
              <input
                type="file"
                name="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.bmp,.webp"
                disabled={catalogUploading}
                className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                  catalogUploading ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              />
              <p className="text-xs text-gray-500 mt-1">
                * 最大50MB，支援 PDF、DOC、XLS、PPT 和圖片格式（JPG、PNG等）
              </p>
            </div>
            <button
              type="submit"
              disabled={catalogUploading}
              className={`font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center ${
                catalogUploading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-700 text-white"
              }`}
            >
              {catalogUploading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  上傳中...
                </>
              ) : (
                "添加新型錄"
              )}
            </button>
          </form>
          {loading ? (
            <div className="flex justify-center">
              <FaSpinner className="animate-spin text-4xl text-blue-500" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      標題
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      描述
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      檔案
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      建立時間
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {catalogs.map((catalog) => (
                    <tr
                      key={catalog.id}
                      className={`hover:bg-gray-50 transition-colors ${
                        catalog.id === lastAddedId ? "bg-yellow-50" : ""
                      }`}
                    >
                      {editingCatalog && editingCatalog.id === catalog.id ? (
                        <td colSpan="5" className="px-6 py-4">
                          <form
                            onSubmit={handleUpdateCatalog}
                            className="space-y-4"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  型錄標題
                                </label>
                                <input
                                  type="text"
                                  name="title"
                                  defaultValue={catalog.title}
                                  placeholder="型錄標題"
                                  required
                                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  檔案上傳
                                </label>
                                <input
                                  type="file"
                                  name="file"
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.bmp,.webp"
                                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  * 留空表示不更新檔案，支援文件和圖片格式
                                </p>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                型錄描述
                              </label>
                              <textarea
                                name="description"
                                defaultValue={catalog.description}
                                placeholder="型錄描述"
                                rows="3"
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition-colors"
                                disabled={loading}
                              >
                                {loading ? "保存中..." : "保存"}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingCatalog(null)}
                                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded transition-colors"
                                disabled={loading}
                              >
                                取消
                              </button>
                            </div>
                          </form>
                        </td>
                      ) : (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {catalog.title}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {catalog.description || "無描述"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {catalog.file_path ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleCatalogDownload(catalog)}
                                  className="text-blue-600 hover:text-blue-900 text-sm font-medium flex items-center"
                                >
                                  下載檔案
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">
                                無檔案
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {new Date(catalog.created_at).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditCatalog(catalog)}
                                className="bg-blue-500 hover:bg-blue-700 text-white text-xs font-bold py-1 px-3 rounded transition-colors"
                              >
                                編輯
                              </button>
                              <button
                                onClick={() =>
                                  setConfirmDelete({
                                    show: true,
                                    id: catalog.id,
                                    type: "catalog",
                                  })
                                }
                                className="bg-red-500 hover:bg-red-700 text-white text-xs font-bold py-1 px-3 rounded flex items-center transition-colors"
                              >
                                <FaTrash className="mr-1 w-3 h-3" /> 刪除
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {catalogs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  暫無型錄資料
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <h2 className="text-2xl font-semibold mb-4">材料圖片管理</h2>
        <form onSubmit={handleMaterialSubmit} className="mb-8">
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="images"
            >
              選擇圖片（可多選）
            </label>
            <input
              type="file"
              name="images"
              multiple
              required
              accept="image/*"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              onChange={handleFileChange}
            />
            <p className="text-xs text-gray-500 mt-1">
              * 最多10個文件，每個文件最大10MB，僅支援圖片格式
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
            {files.map((file, index) => (
              <div key={index} className="border rounded-lg p-2">
                <div className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt="預覽"
                    className="w-full h-32 object-cover mb-2 rounded"
                  />
                  <button
                    type="button"
                    onClick={() => handleRotateImage(index)}
                    className="absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-700 p-1 rounded-full shadow-md transition duration-200"
                    title="旋轉圖片"
                  >
                    <FaRedo className="w-4 h-4" />
                  </button>
                  {imageRotations[index] !== undefined && (
                    <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                      {imageRotations[index]}°
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={newMaterial.alts[index] || ""}
                  onChange={(e) => handleAltChange(index, e.target.value)}
                  placeholder="圖片描述"
                  className="w-full mb-2 p-1 border rounded"
                />
                <button
                  type="button"
                  onClick={() => toggleDescription(index)}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-1 px-2 rounded"
                >
                  {expandedDescriptions[index] ? "收起描述" : "展開描述"}
                </button>
                {expandedDescriptions[index] && (
                  <textarea
                    value={newMaterial.descriptions[index] || ""}
                    onChange={(e) =>
                      handleDescriptionChange(index, e.target.value)
                    }
                    placeholder="材料描述"
                    className="w-full mt-2 p-1 border rounded"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="page"
            >
              選擇頁面
            </label>
            <select
              value={newMaterial.page}
              onChange={(e) =>
                setNewMaterial({ ...newMaterial, page: e.target.value })
              }
              required
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            >
              <option value="">選擇頁面</option>
              {pages.map((page) => (
                <option key={page} value={page}>
                  {page}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            添加新材料圖片
          </button>
        </form>
      </div>

      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <h2 className="text-2xl font-semibold mb-4">頁面說明和材料管理</h2>
        {pages.map((page, index) => (
          <div key={page} className="mb-4">
            <button
              onClick={() => togglePageExpand(page)}
              className="w-full bg-gray-200 hover:bg-gray-300 text-left text-gray-800 font-semibold py-2 px-4 rounded flex justify-between items-center"
            >
              <span>{page}</span>
              <span className="text-sm">
                ({materials.filter((m) => m.page === page).length} 個材料)
              </span>
            </button>
            {expandedPages[page] && (
              <div className="mt-4">
                <form
                  onSubmit={(e) => handlePageDescriptionSubmit(e, page)}
                  className="mb-4"
                >
                  <textarea
                    value={pageDescriptions[page] || ""}
                    onChange={(e) =>
                      setPageDescriptions({
                        ...pageDescriptions,
                        [page]: e.target.value,
                      })
                    }
                    placeholder="頁面說明"
                    required
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                  />
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  >
                    更新頁面說明
                  </button>
                </form>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {materials
                    .filter((material) => material.page === page)
                    .map((material) => (
                      <div
                        key={material.id}
                        className={`border p-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg ${
                          material.id === lastAddedId ? "bg-yellow-100" : ""
                        }`}
                      >
                        {editingMaterial &&
                        editingMaterial.id === material.id ? (
                          <form onSubmit={handleUpdateMaterial}>
                            <div className="relative mb-4">
                              <img
                                src={
                                  editingImageData
                                    ? URL.createObjectURL(editingImageData)
                                    : `${
                                        process.env.REACT_APP_API_URL
                                      }/uploads/${material.image
                                        .split("/")
                                        .pop()}`
                                }
                                alt={material.alt}
                                className="w-full h-48 object-cover rounded-lg"
                              />
                              <button
                                type="button"
                                onClick={handleEditImageRotate}
                                className="absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-700 p-2 rounded-full shadow-md transition duration-200"
                                title="旋轉圖片"
                                disabled={loading}
                              >
                                <FaRedo className="w-4 h-4" />
                              </button>
                              {editingImageRotation !== 0 && (
                                <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                  {editingImageRotation}°
                                </div>
                              )}
                            </div>
                            <input
                              type="text"
                              name="alt"
                              defaultValue={material.alt}
                              placeholder="圖片描述"
                              className="mb-2 w-full p-2 border rounded"
                            />
                            <textarea
                              name="description"
                              defaultValue={material.description}
                              placeholder="材料描述"
                              className="mb-2 w-full p-2 border rounded"
                            />
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                                disabled={loading}
                              >
                                {loading ? "保存中..." : "保存"}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingMaterial(null);
                                  setEditingImageRotation(0);
                                  setEditingImageData(null);
                                }}
                                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                                disabled={loading}
                              >
                                取消
                              </button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <img
                              src={`${
                                process.env.REACT_APP_API_URL
                              }/uploads/${material.image.split("/").pop()}`}
                              alt={material.alt}
                              className="mb-2 w-full h-48 object-cover rounded-lg"
                            />
                            <p className="text-gray-700 text-base">
                              {material.alt}
                            </p>
                            <div className="flex justify-between mt-2">
                              <button
                                onClick={() => handleEdit(material)}
                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center justify-center transition duration-300 ease-in-out"
                              >
                                編輯
                              </button>
                              <button
                                onClick={() =>
                                  setConfirmDelete({
                                    show: true,
                                    id: material.id,
                                    type: "material",
                                  })
                                }
                                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded flex items-center justify-center transition duration-300 ease-in-out"
                              >
                                <FaTrash className="mr-2" /> 刪除
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {confirmDelete.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="bg-white p-5 rounded-lg shadow-xl">
            <h3 className="text-lg font-bold mb-4">確認刪除</h3>
            <p>您確定要刪除這個項目嗎？</p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() =>
                  setConfirmDelete({ show: false, id: null, type: "" })
                }
                className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded mr-2"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                確認刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
